import React, { useEffect, useState } from 'react';
import { AppShell } from '@/components/nav/AppShell';
import { propertiesApi, customersApi } from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Plus, Home as HomeIcon, RefreshCw } from 'lucide-react';
import { PROPERTIES } from '@/constants/testIds';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { EmptyState } from '@/components/empty/EmptyState';
import { ListSkeleton } from '@/components/common/ListSkeleton';
import { Badge } from '@/components/ui/badge';
import { fmtDate } from '@/lib/format';

export default function PropertiesPage() {
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [customers, setCustomers] = useState([]);
  const [openNew, setOpenNew] = useState(false);
  const [form, setForm] = useState({
    customer_id: '', nickname: '', address: '', bedrooms: 1, bathrooms: 1, sqft: '', timezone: 'UTC',
    ical_url: '', google_calendar_ical_url: '', buffer_after_hours: 1, default_cleaning_hours: 3, notes: '',
  });

  const load = async () => {
    try {
      const [ps, cs] = await Promise.all([propertiesApi.list(), customersApi.list()]);
      setItems(ps);
      setCustomers(cs);
    } catch {
      // surfaced by the api response interceptor
    } finally {
      setLoaded(true);
    }
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...form,
        bedrooms: parseInt(form.bedrooms) || 0,
        bathrooms: parseInt(form.bathrooms) || 0,
        sqft: form.sqft ? parseInt(form.sqft) : null,
        buffer_after_hours: parseInt(form.buffer_after_hours) || 0,
        default_cleaning_hours: parseInt(form.default_cleaning_hours) || 3,
      };
      await propertiesApi.create(payload);
      toast.success('Property created');
      setOpenNew(false);
      setForm({ customer_id: '', nickname: '', address: '', bedrooms: 1, bathrooms: 1, sqft: '', timezone: 'UTC', ical_url: '', google_calendar_ical_url: '', buffer_after_hours: 1, default_cleaning_hours: 3, notes: '' });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create');
    }
  };

  const sync = async (id) => {
    try {
      const r = await propertiesApi.syncIcal(id);
      toast.success(`Synced: ${r.jobs_created} created, ${r.jobs_updated} updated, ${r.jobs_cancelled} cancelled`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Sync failed');
    }
  };

  return (
    <AppShell title="Properties">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between [&>*:last-child]:w-full sm:[&>*:last-child]:w-auto mb-6">
        <div>
          <h1 className="font-display text-2xl md:text-3xl font-semibold">Properties</h1>
          <p className="text-sm text-muted-foreground">Manage properties, iCal sync, checklists and inventory.</p>
        </div>
        <Dialog open={openNew} onOpenChange={setOpenNew}>
          <DialogTrigger asChild>
            <Button data-testid={PROPERTIES.createButton}><Plus className="h-4 w-4 mr-2" />New property</Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>New property</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-3">
              <div>
                <Label>Customer *</Label>
                <select
                  className="w-full h-10 sm:h-9 rounded-md border border-input bg-background px-3 text-sm shadow-xs transition-colors hover:border-border-strong"
                  value={form.customer_id}
                  onChange={e => setForm({ ...form, customer_id: e.target.value })}
                  data-testid={PROPERTIES.formCustomer}
                  required
                >
                  <option value="">Select customer</option>
                  {customers.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
              <div><Label>Nickname *</Label><Input required placeholder="Ocean View Villa" value={form.nickname} onChange={e => setForm({ ...form, nickname: e.target.value })} data-testid={PROPERTIES.formNickname} /></div>
              <div><Label>Address</Label><Input value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} data-testid={PROPERTIES.formAddress} /></div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><Label>Bedrooms</Label><Input type="number" min="0" value={form.bedrooms} onChange={e => setForm({ ...form, bedrooms: e.target.value })} data-testid={PROPERTIES.formBedrooms} /></div>
                <div><Label>Bathrooms</Label><Input type="number" min="0" value={form.bathrooms} onChange={e => setForm({ ...form, bathrooms: e.target.value })} data-testid={PROPERTIES.formBathrooms} /></div>
                <div><Label>Sqft</Label><Input type="number" min="0" value={form.sqft} onChange={e => setForm({ ...form, sqft: e.target.value })} data-testid={PROPERTIES.formSqft} /></div>
              </div>
              <div><Label>iCal URL (Airbnb / VRBO)</Label><Input placeholder="https://www.airbnb.com/calendar/ical/…" value={form.ical_url} onChange={e => setForm({ ...form, ical_url: e.target.value })} data-testid={PROPERTIES.formIcal} /></div>
              <div><Label>Google Calendar iCal URL (optional)</Label><Input placeholder="https://calendar.google.com/calendar/ical/…/basic.ics" value={form.google_calendar_ical_url} onChange={e => setForm({ ...form, google_calendar_ical_url: e.target.value })} /></div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Buffer after checkout (hrs)</Label><Input type="number" min="0" value={form.buffer_after_hours} onChange={e => setForm({ ...form, buffer_after_hours: e.target.value })} data-testid={PROPERTIES.formBuffer} /></div>
                <div><Label>Default cleaning (hrs)</Label><Input type="number" min="1" value={form.default_cleaning_hours} onChange={e => setForm({ ...form, default_cleaning_hours: e.target.value })} data-testid={PROPERTIES.formDuration} /></div>
              </div>
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setOpenNew(false)}>Cancel</Button>
                <Button type="submit" data-testid={PROPERTIES.formSubmit}>Create</Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!loaded ? (
        <ListSkeleton />
      ) : items.length === 0 ? (
        <EmptyState
          title="No properties yet"
          description="Add properties for your customers to start scheduling cleanings."
          cta={<Button onClick={() => setOpenNew(true)}><Plus className="h-4 w-4 mr-2" />New property</Button>}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map(p => (
            <Card key={p.id} className="hover:border-primary/50 transition-colors">
              <CardContent className="p-4 space-y-3">
                <Link to={`/properties/${p.id}`} className="block">
                  <div className="flex items-center gap-2 font-semibold"><HomeIcon className="h-4 w-4 text-primary" />{p.nickname}</div>
                  <div className="text-xs text-muted-foreground mt-1">{p.address || '—'}</div>
                  <div className="mt-2 text-xs text-muted-foreground">{p.bedrooms} bd · {p.bathrooms} ba · {p.sqft ? `${p.sqft} sqft` : '—'}</div>
                </Link>
                <div className="flex items-center justify-between border-t pt-2">
                  <div className="text-[11px] text-muted-foreground">
                    {p.ical_url ? <>iCal: {p.last_ical_sync ? fmtDate(p.last_ical_sync) : 'Never synced'}</> : <>No iCal</>}
                  </div>
                  {p.ical_url && (
                    <Button size="sm" variant="outline" onClick={() => sync(p.id)} data-testid={PROPERTIES.syncButton}>
                      <RefreshCw className="h-3 w-3 mr-1" />Sync
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </AppShell>
  );
}
